import { filter, includes, map, sortBy, transform } from 'lodash';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ordersMonitoringApi } from 'api';
import {
  DataWrapper,
  PageHeader,
  QueryFilters,
  QueryColumns,
} from 'components';
import { DefaultPaymentTypeCode, FilterDefinitionType, QueryKey } from 'enums';
import { useCurrencies, usePartialQuery, useUserContext } from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  FilterDefinition,
  MonitoringDataItem,
  PayinOrdersMonitoringFilters,
  RangeMonitoringDataRow,
} from 'types';
import { orderMonitoringUtils } from 'utils';

import { OrdersMonitoringPayinRange } from './Range';

export const TraderPayinOrdersMonitoringPage: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Common);
  const { fiatCurrenciesOptions, getFiatCurrencyCode } = useCurrencies();
  const { paymentTypes } = useUserContext();

  const queryResult = usePartialQuery(
    QueryKey.PayinOrdersMonitoring,
    ordersMonitoringApi.getPayinAsTrader,
  );

  const availablePaymentTypes = useMemo(
    () =>
      filter(paymentTypes, (paymentType) =>
        includes(
          queryResult.data?.availablePaymentTypes,
          paymentType.code as DefaultPaymentTypeCode,
        ),
      ),
    [paymentTypes, queryResult.data?.availablePaymentTypes],
  );

  const availableColumns = useMemo(
    () =>
      map(availablePaymentTypes, (paymentType) => ({
        value: paymentType.code,
        label: orderMonitoringUtils.getPaymentTypeHeader(
          paymentType.code as DefaultPaymentTypeCode,
        ),
      })),
    [availablePaymentTypes],
  );

  const rangeData = useMemo(
    () =>
      transform(
        queryResult?.data?.monitoringData || [],
        (acc, item) => {
          const rangeLabel = orderMonitoringUtils.getRangeLabel({
            min: item.minAmount,
            max: item.maxAmount,
          });
          const prev = acc[rangeLabel] || {};
          acc[rangeLabel] = {
            ...prev,
            minAmount: item.minAmount,
            [item.paymentTypeCode]: {
              alertLevel: item.alertLevel,
              label: item.label,
            },
          } as any;
        },
        {} as Record<string, RangeMonitoringDataRow<MonitoringDataItem>>,
      ),
    [queryResult?.data?.monitoringData],
  );

  const sortedRanges = useMemo(
    () =>
      sortBy(
        Object.entries(rangeData),
        ([rangeLabel, rangeData]) => rangeData.minAmount,
      ),
    [rangeData],
  );

  const filtersDefinitions: FilterDefinition<PayinOrdersMonitoringFilters>[] =
    useMemo(
      () => [
        {
          label: t('filters.fiat_currency'),
          name: 'fiatCurrencyId',
          type: FilterDefinitionType.Select,
          options: fiatCurrenciesOptions,
          getDisplayName: getFiatCurrencyCode,
        },
      ],
      [t, fiatCurrenciesOptions, getFiatCurrencyCode],
    );

  return (
    <Fragment>
      <PageHeader title={t('features.orders_monitoring.title')} />
      <div className="tw-flex tw-items-center tw-justify-end">
        <div className="tw-flex-1">
          <QueryFilters filtersDefinitions={filtersDefinitions} />
        </div>
        <QueryColumns
          title={t('features.orders_monitoring.columns_toggle_title')}
          availableColumns={availableColumns}
        />
      </div>

      <DataWrapper queryResult={queryResult}>
        <Fragment>
          {map(sortedRanges, ([rangeLabel, rangeData]) => (
            <OrdersMonitoringPayinRange
              key={rangeLabel}
              rangeLabel={rangeLabel}
              rangeData={rangeData}
              availablePaymentTypes={queryResult?.data?.availablePaymentTypes}
              className="tw-mb-6"
            />
          ))}
        </Fragment>
      </DataWrapper>
    </Fragment>
  );
};
